import { createContext, useContext, useMemo } from "react";
import type { CSSProperties, ReactNode } from "react";

import { Z_INDEX_VALUE } from "./z-index";
import type { ZIndexLayer } from "./z-index";

/**
 * Nesting-aware z-index for portaled overlays (dialogs, popovers, tooltips, sheets, …).
 *
 * ## The problem this solves
 *
 * A flat, global z-index scale cannot satisfy both of these at once, because they demand the
 * opposite ordering of the *same two layers*:
 *
 * - A **popover rendered inside a dialog** must sit **above** that dialog.
 * - A **dialog opened from (triggered by) a popover** must sit **above** that popover.
 *
 * With one fixed number per layer, `popover` is either always-above-`modal` or always-below — one
 * of the two cases is always wrong. The fix is to stop treating a layer's number as an *absolute*
 * rank and treat it as an **offset added to the z-index of the enclosing overlay**. An overlay's
 * effective z-index then depends on *where it sits in the React tree*, not on a global constant:
 *
 * - The popover is portaled **inside** the dialog's subtree, so it inherits the dialog's (high)
 *   base and lands above it.
 * - The triggered dialog is a **new top-level overlay** — it is not rendered inside the popover's
 *   subtree — so it inherits the root base and starts a fresh, higher stack above the popover.
 *
 * ## Why this keys off React tree position (and why that makes it hybrid-safe)
 *
 * Because the base flows through React **context**, correctness depends only on tree nesting, not
 * on which package a component came from. During the `@tellescope/ui` → `@tellescope/design-system`
 * migration both libraries are on screen together; a DS popover inside a UI dialog (or vice-versa)
 * stacks correctly **as long as every overlay reads and writes THIS one context instance**. That is
 * why this lives in the design system (the lower tier) and `@tellescope/ui` imports it rather than
 * defining its own — two separate contexts would break exactly at the package seam.
 *
 * ## Why the z-index is applied as a CSS variable, not a Tailwind class
 *
 * The computed value (`base + offset`) is dynamic, so it cannot be a statically-scanned Tailwind
 * class — Tailwind only emits classes it can see verbatim in source, and `` `z-[${n}]` `` is
 * invisible to the scanner. Instead the number is written to an inline `style={{ "--z": n }}` CSS
 * custom property, and a single **static** class `z-[var(--z)]` (which the scanner *does* see, once,
 * from this file's {@link OVERLAY_Z_INDEX_CLASS}) reads it. This is the sanctioned bridge between
 * "must be computed" and "must be scannable" — see the note on {@link Z_INDEX_VALUE}.
 *
 * ⚠️ **Do not remove this during a token/style audit.** The inline `--z` style and the
 * `z-[var(--z)]` class are not stray hardcoded z-indexes to tokenize away — they are the mechanism
 * that makes nested overlays stack correctly. Tokenizing them back into fixed classes reintroduces
 * the exact bug described above.
 */

/**
 * The z-index the current overlay subtree stacks relative to. `0` means "document root" (no
 * enclosing overlay). Each overlay adds its layer offset to this and provides the result to its own
 * portaled children via {@link OverlayLayerProvider}.
 *
 * Exported so `@tellescope/ui` overlays can consume the SAME context during migration — do not
 * duplicate this context in another package (see the hybrid-safety note above).
 */
export const OverlayLayerContext = createContext<number>(0);

/** Return shape of {@link useOverlayZIndex}: the computed value plus ready-to-spread render props. */
export interface OverlayZIndex {
  /** The computed absolute z-index (`inheritedBase + layer offset`). */
  z: number;
  /** Spread onto the overlay element: sets the `--z` custom property the static class reads. */
  style: CSSProperties;
  /** The static, scanner-visible Tailwind class that reads `--z`. Merge via `cn()`. */
  className: string;
}

/**
 * The single static Tailwind class that binds an element's z-index to the `--z` custom property.
 * Declared here as a literal so the scanner emits `z-[var(--z)]` exactly once for the whole system.
 */
export const OVERLAY_Z_INDEX_CLASS = "z-[var(--z)]";

/**
 * Compute a nesting-aware z-index for an overlay of the given semantic `layer`.
 *
 * Reads the inherited base from {@link OverlayLayerContext} and adds the layer's offset from
 * {@link Z_INDEX_VALUE}. Returns `{ z, style, className }` — spread `style` and merge `className`
 * onto the portaled overlay element. To make descendants stack above this overlay, wrap the
 * portaled children in {@link OverlayLayerProvider} with the returned `z`.
 *
 * @example
 * ```tsx
 * const { style, className, z } = useOverlayZIndex("popover");
 * return (
 *   <Portal>
 *     <OverlayLayerProvider base={z}>
 *       <Content style={style} className={cn("…", className)}>{children}</Content>
 *     </OverlayLayerProvider>
 *   </Portal>
 * );
 * ```
 */
export const useOverlayZIndex = (layer: ZIndexLayer): OverlayZIndex => {
  const inheritedBase = useContext(OverlayLayerContext);
  const z = inheritedBase + Z_INDEX_VALUE[layer];

  return useMemo(
    () => ({
      z,
      // Inline style (not a Tailwind class) because `z` is computed; see the file-level note.
      style: { "--z": z } as CSSProperties,
      className: OVERLAY_Z_INDEX_CLASS,
    }),
    [z]
  );
};

/**
 * Provide `base` as the z-index every nested overlay stacks relative to. Wrap an overlay's portaled
 * children in this using the `z` from {@link useOverlayZIndex} so anything opened *inside* the
 * overlay (a popover in a dialog, a tooltip in a popover) lands above it.
 */
export const OverlayLayerProvider = ({ base, children }: { base: number; children: ReactNode }) => (
  <OverlayLayerContext.Provider value={base}>{children}</OverlayLayerContext.Provider>
);
